import { useMemo } from 'react'

import { useMilesEstimate } from '@src/hooks/useMilesEstimate'
import { IExchangeData } from '@src/types/exchange'
import { IFee } from '@src/types/fee'
import { getDisabledChains, isDestinationSolana, isDestinationTron, isOriginSolana } from '@src/utils/chainUtils'
import { shortenSolanaAddress } from '@src/utils/exchangeHelpers'
import { computeHasErrors, isPotentiallyCCTP } from '@src/utils/exchangeUtils'
import { formatFeeDisplay } from '@src/utils/formatFee'
import { truncateAddress } from '@src/utils/truncateAddress'

interface UseExchangeMemosProps {
    sellData: IExchangeData
    buyData: IExchangeData
    isInsufficientBalance: boolean
    isQuoteInvalid: boolean
    isBuyOutputLoading: boolean
    isQuoteExpired: boolean
    address?: string
    solanaWalletAddress?: string
    machFee: number
}

export const useExchangeMemos = ({
    sellData,
    buyData,
    isInsufficientBalance,
    isQuoteInvalid,
    isBuyOutputLoading,
    isQuoteExpired,
    address,
    solanaWalletAddress,
    machFee,
}: UseExchangeMemosProps) => {
    const isCCTP = useMemo(
        () => isPotentiallyCCTP(sellData?.token?.symbol, buyData?.token?.symbol, sellData?.chainId, buyData?.chainId),
        [buyData?.chainId, buyData?.token?.symbol, sellData?.chainId, sellData?.token?.symbol]
    )

    const isOriginSol = useMemo(() => isOriginSolana(sellData?.chainId), [sellData?.chainId])
    const isDestinationSol = useMemo(() => isDestinationSolana(buyData?.chainId), [buyData?.chainId])
    const isDestinationTronChain = useMemo(() => isDestinationTron(buyData?.chainId), [buyData?.chainId])

    const disabledChains = useMemo(() => getDisabledChains(sellData, buyData), [sellData, buyData])

    const hasErrors = useMemo(
        () =>
            computeHasErrors(
                isInsufficientBalance,
                isQuoteInvalid,
                isBuyOutputLoading,
                isQuoteExpired,
                isDestinationSol,
                sellData?.token?.symbol
            ),
        [
            isInsufficientBalance,
            isQuoteInvalid,
            isBuyOutputLoading,
            isQuoteExpired,
            isDestinationSol,
            sellData?.token?.symbol,
        ]
    )

    // Use the new cached miles estimation hook
    const { data: computedMilesAndMultiplier = { miles: 0, multiplier: 1 } } = useMilesEstimate({
        sellData,
        buyData,
        walletAddress: address,
        enabled: !hasErrors,
    })

    const shortenedAddress = useMemo(() => {
        if (isDestinationSol) return shortenSolanaAddress(solanaWalletAddress || '')
        else if (isDestinationTronChain) return truncateAddress(address || '')
        return ''
    }, [solanaWalletAddress, address, isDestinationSol, isDestinationTronChain])

    const feesDisplay: IFee[] = useMemo(() => {
        return [
            {
                label: 'Mach Fee',
                value: machFee,
                display: formatFeeDisplay(machFee, sellData.token?.symbol),
            },
        ]
    }, [machFee, sellData.token?.symbol])

    return {
        isCCTP,
        isOriginSol,
        isDestinationSol,
        isDestinationTronChain,
        disabledChains,
        hasErrors,
        computedMilesAndMultiplier,
        shortenedAddress,
        feesDisplay,
    }
}
